// © Microsoft Corporation. All rights reserved.

import { PerfTimeline, tryGetPerfTimeline } from "@msnews/diagnostics";
import { dispatchAction, getActionPayload } from "./ActionDispatchHelper";

import { ActionSenderBase } from "./ActionSenderBase";
import { IActionDispatchCancellationToken } from "./IActionDispatchCancellationToken";
import { IActionPayload } from "./IActionPayload";
import { Observer } from "./ActionObserver";

/**
 * Class that wraps the sending of a action globally to all connectors
 * @export
 * @class
 */
export class GlobalActionSender<TActionSignature extends Function> extends ActionSenderBase<TActionSignature> {

    /**
     * Creates an instance of the `GlobalActionSender` class.
     * @constructor
     * @param {string} type The action type.
     * @param {string} experienceInstanceId Optional experienceInstanceId of the experience that is originating the action.
     * @param {IActionDispatchCancellationToken} cancellationToken Optional cancellation token, which can be used to cancel an in flight action.
     */
    constructor(
        type: string,
        observers: Observer[],
        private experienceInstanceId?: string,
        cancellationToken?: IActionDispatchCancellationToken) {

        super(type, observers, cancellationToken);
    }

    /**
     * Core dispatch implementation for {GlobalActionSender}
     * @param args - the arguments being dispatched
     * @returns dispatch result metadata
     */
    protected getDispatchResult(args: any[]): { payload: IActionPayload, result: any } {
        const payload = getActionPayload(undefined, this.type, args, this.experienceInstanceId);
        const result = dispatchAction(undefined, payload);
        return { payload, result };
    }

    /**
     * Gets the perf timeline if applicable
     */
    protected tryGetPerfTimeline(): PerfTimeline {
        return tryGetPerfTimeline("GlobalActionSender", this.experienceInstanceId || "unknown");
    }
}